import React from 'react';
import { AuroraBackground } from './ui/shadcn-io/aurora-background';

const FeatureCardsGrid: React.FC = () => {
  return (
    <div className="mb-12">
      {/* Bento Grid Layout: 2 rows × 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 mb-6">

        {/* Card 1: Multi-Agent Orchestration (TALL - spans 2 rows) - Aurora Background */}
        <div className="relative overflow-hidden rounded-3xl md:row-span-2 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 min-h-[400px]">
          <AuroraBackground className="h-full rounded-3xl" showRadialGradient={false}>
            <div className="relative z-10 h-full flex flex-col p-10">
              {/* Icon */}
              <div className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-blue-100">
                <svg className="w-11 h-11 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-3" style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-heading)' }}>
                Multi-Agent Orchestration
              </h3>
              <p className="text-gray-600 leading-relaxed flex-grow" style={{ fontSize: 'var(--font-size-base)' }}>
                Intelligent supervisor coordinates specialized agents for different evaluation dimensions. Dynamic routing ensures efficient processing with parallel execution for optimal performance.
              </p>

              {/* Feature Tags */}
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Instruction-led</span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Fine-tuned & calibrated</span>
              </div>
            </div>
          </AuroraBackground>
        </div>

        {/* Card 2: AI-Powered Fair Reviews - Mint Green */}
        <div className="relative overflow-hidden rounded-3xl p-8 shadow-sm border border-gray-200 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 hover:shadow-md transition-all duration-300 min-h-[200px]">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-green-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10">
            {/* Icon */}
            <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-green-100">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>

            {/* Content */}
            <h3 className="font-semibold text-gray-900 mb-2" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-heading)' }}>
              Objective Guidance
            </h3>
            <p className="text-gray-600 leading-relaxed" style={{ fontSize: 'var(--font-size-base)' }}>
              Provides objective, guideline-based insights to support fair decisions.
            </p>
          </div>
        </div>

        {/* Card 3: Time-Saving Insights - Lavender Purple */}
        <div className="relative overflow-hidden rounded-3xl p-8 shadow-sm border border-gray-200 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 hover:shadow-md transition-all duration-300 min-h-[200px]">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-purple-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10">
            {/* Icon */}
            <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-100">
              <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            {/* Content */}
            <h3 className="font-semibold text-gray-900 mb-2" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-heading)' }}>
              Time-Saving Insights
            </h3>
            <p className="text-gray-600 leading-relaxed" style={{ fontSize: 'var(--font-size-base)' }}>
              Results in minutes, not hours. Automated 6-dimension analysis.
            </p>
          </div>
        </div>

        {/* Card 4: LLM Grounding - Amber/Orange */}
        <div className="relative overflow-hidden rounded-3xl p-8 shadow-sm border border-gray-200 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 hover:shadow-md transition-all duration-300 min-h-[200px]">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-orange-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10">
            {/* Icon */}
            <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-100">
              <svg className="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
              </svg>
            </div>

            {/* Content */}
            <h3 className="font-semibold text-gray-900 mb-2" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-heading)' }}>
              LLM Grounding
            </h3>
            <p className="text-gray-600 leading-relaxed" style={{ fontSize: 'var(--font-size-base)' }}>
              Reduces hallucinations with data-backed evaluation techniques.
            </p>
          </div>
        </div>

        {/* Card 5: Human-Centered - Rose Pink */}
        <div className="relative overflow-hidden rounded-3xl p-8 shadow-sm border border-gray-200 bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 hover:shadow-md transition-all duration-300 min-h-[200px]">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-rose-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

          <div className="relative z-10">
            {/* Icon */}
            <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-100">
              <svg className="w-8 h-8 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>

            {/* Content */}
            <h3 className="font-semibold text-gray-900 mb-2" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-heading)' }}>
              Human-Centered
            </h3>
            <p className="text-gray-600 leading-relaxed" style={{ fontSize: 'var(--font-size-base)' }}>
              Augments judgment, never replaces human decision-making.
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer Banner */}
      <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 shadow-sm">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,#fff,rgba(255,255,255,0.6))] bg-[size:20px_20px]"></div>
        <div className="relative flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-blue-900 mb-1" style={{ fontSize: 'var(--font-size-base)' }}>Important Note</p>
            <p className="text-gray-700 leading-relaxed" style={{ fontSize: 'var(--font-size-base)' }}>
              TalRev is designed to support and enhance human decision-making in talent reviews, not replace it.
              All AI-generated insights should be reviewed and validated by experienced reviewers as part of a comprehensive evaluation process.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureCardsGrid;
